import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Archive, ArchiveRestore, FileBarChart, FolderPlus, ExternalLink } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export const Hierarchy = ({ kind }) => {
  const isPortfolio = kind === "portfolio";
  const path = isPortfolio ? "/portfolios" : "/programmes";
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [managing, setManaging] = useState(null);
  const [form, setForm] = useState({});

  const loadProjects = () => api.get("/projects").then(({ data }) => setProjects(data));

  const toggleProject = async (project, attach) => {
    const key = isPortfolio ? "portfolio_id" : "programme_id";
    const other = isPortfolio ? "programme_id" : "portfolio_id";
    try {
      await api.patch(`/projects/${project.id}/assign`, {
        [key]: attach ? managing.id : null,
        [other]: attach && isPortfolio ? project[other] || null : null,
      });
      await loadProjects();
      toast.success(attach ? `${project.name} added to ${managing.name}` : `${project.name} removed`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const load = (archived = showArchived) =>
    api.get(`${path}?archived=${archived}`).then(({ data }) => setItems(data));

  useEffect(() => {
    load(showArchived);
    loadProjects();
    if (!isPortfolio) api.get("/portfolios").then(({ data }) => setPortfolios(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, showArchived]);

  const save = async () => {
    if (!form.name?.trim()) return toast.error("Name is required");
    try {
      await api.post(path, form);
      toast.success(`${isPortfolio ? "Portfolio" : "Programme"} created`);
      setOpen(false);
      setForm({});
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const act = async (url, message) => {
    try {
      await api.post(url);
      await load();
      toast.success(message);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`${path}/${id}`);
      load();
      toast.success("Deleted");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const count = (id) => projects.filter((p) => (isPortfolio ? p.portfolio_id : p.programme_id) === id).length;

  return (
    <Shell
      testId={`${kind}s-page`}
      title={isPortfolio ? "Portfolios" : "Programmes"}
      subtitle={showArchived
        ? `Archived ${kind}s — restore anything you need back in play`
        : isPortfolio ? "Top level of the delivery hierarchy" : "Groups of related projects within a portfolio"}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            data-testid={`toggle-archived-${kind}-btn`}
            onClick={() => setShowArchived((v) => !v)}
            className="gap-2"
          >
            <Archive size={15} /> {showArchived ? "Show Active" : "Show Archived"}
          </Button>
          <Button data-testid={`new-${kind}-btn`} onClick={() => { setForm({}); setOpen(true); }} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
            <Plus size={15} /> New {isPortfolio ? "Portfolio" : "Programme"}
          </Button>
        </div>
      }
    >
      {items.length === 0 ? (
        <Empty
          testId={`${kind}s-empty`}
          text={showArchived ? `No archived ${kind}s.` : `No ${kind}s yet. Create one to structure your delivery portfolio.`}
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`${kind}-card-${it.id}`} className="panel p-5 transition-shadow duration-200 hover:shadow-md">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-display text-base font-semibold text-slate-900">{it.name}</h3>
                  <div className="mt-0.5 text-xs text-slate-400">
                    {isPortfolio ? it.owner || "No owner" : it.manager || "No manager"}
                    {!isPortfolio && it.portfolio_id && ` · ${portfolios.find((p) => p.id === it.portfolio_id)?.name || ""}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {it.is_archived ? (
                    <button data-testid={`restore-${kind}-${it.id}`} onClick={() => act(`${path}/${it.id}/restore`, "Restored")} aria-label={`Restore ${kind}`} title={`Restore ${kind}`} className="text-slate-400 transition-colors hover:text-emerald-600">
                      <ArchiveRestore size={15} />
                    </button>
                  ) : (
                    <button data-testid={`archive-${kind}-${it.id}`} onClick={() => act(`${path}/${it.id}/archive`, "Archived — restore it any time")} aria-label={`Archive ${kind}`} title={`Archive ${kind}`} className="text-slate-300 transition-colors hover:text-amber-600">
                      <Archive size={15} />
                    </button>
                  )}
                  <button data-testid={`delete-${kind}-${it.id}`} onClick={() => window.confirm(`Delete ${it.name}? Archiving is usually safer.`) && remove(it.id)} aria-label={`Delete ${kind}`} title={`Delete ${kind}`} className="text-slate-300 transition-colors hover:text-red-600">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              {it.description && <p className="mt-3 text-sm leading-relaxed text-slate-600">{it.description}</p>}
              <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
                <button
                  data-testid={`${kind}-project-count-${it.id}`}
                  onClick={() => navigate(`/projects?${isPortfolio ? "portfolio" : "programme"}=${it.id}`)}
                  className="text-xs font-semibold uppercase tracking-wider text-slate-500 underline-offset-2 transition-colors hover:text-slate-900 hover:underline"
                >
                  {count(it.id)} project{count(it.id) === 1 ? "" : "s"} →
                </button>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" data-testid={`manage-projects-${it.id}`} onClick={() => setManaging(it)} className="gap-1.5">
                    <FolderPlus size={14} /> Projects
                  </Button>
                  {!isPortfolio && (
                    <Button size="sm" variant="outline" data-testid={`programme-report-btn-${it.id}`} onClick={() => navigate(`/programmes/${it.id}/report`)} className="gap-1.5">
                      <FileBarChart size={14} /> Status Report
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${kind}-dialog`}>
          <DialogHeader><DialogTitle>New {isPortfolio ? "Portfolio" : "Programme"}</DialogTitle>
            <DialogDescription>
              {isPortfolio ? "Portfolios sit at the top of the delivery hierarchy." : "Programmes group related projects inside a portfolio."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><label className="field-label">Name *</label><Input data-testid={`${kind}-name-input`} value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            {isPortfolio ? (
              <div><label className="field-label">Owner</label><Input data-testid="portfolio-owner-input" value={form.owner || ""} onChange={(e) => setForm({ ...form, owner: e.target.value })} /></div>
            ) : (
              <>
                <div><label className="field-label">Programme Manager</label><Input data-testid="programme-manager-input" value={form.manager || ""} onChange={(e) => setForm({ ...form, manager: e.target.value })} /></div>
                <div>
                  <label className="field-label">Parent Portfolio</label>
                  <Select testId="programme-portfolio-select" value={form.portfolio_id} onChange={(v) => setForm({ ...form, portfolio_id: v })} options={portfolios.map((p) => ({ value: p.id, label: p.name }))} placeholder="Select portfolio" />
                </div>
              </>
            )}
            <div><label className="field-label">Description</label><Textarea data-testid={`${kind}-description-input`} rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid={`${kind}-cancel-btn`} onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid={`${kind}-save-btn`} onClick={save} style={{ backgroundColor: "var(--brand-primary)" }}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!managing} onOpenChange={(o) => !o && setManaging(null)}>
        <DialogContent data-testid="manage-projects-dialog" className="max-h-[80vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Projects in {managing?.name}</DialogTitle>
            <DialogDescription>
              Tick a project to attach it to this {kind}.
              {!isPortfolio && " Attaching to a programme also sets the project's portfolio."}
            </DialogDescription>
          </DialogHeader>
          {projects.length === 0 ? (
            <p className="text-sm text-slate-500">No projects exist yet. Create one from All Projects first.</p>
          ) : (
            <div className="space-y-1">
              {projects.map((p) => {
                const attached = (isPortfolio ? p.portfolio_id : p.programme_id) === managing?.id;
                return (
                  <div key={p.id} data-testid={`manage-project-row-${p.id}`} className="flex items-center justify-between gap-3 rounded px-2 py-2 text-sm transition-colors hover:bg-slate-50">
                    <span className="flex items-center gap-2.5">
                      <input
                        data-testid={`manage-project-checkbox-${p.id}`}
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer"
                        checked={attached}
                        onChange={(e) => toggleProject(p, e.target.checked)}
                      />
                      <span className="font-medium text-slate-800">{p.name}</span>
                      <span className="text-xs text-slate-400">{p.ref || p.code}</span>
                    </span>
                    <button
                      data-testid={`manage-project-open-${p.id}`}
                      onClick={() => navigate(`/projects/${p.id}`)}
                      aria-label={`Open ${p.name}`}
                      title={`Open ${p.name}`}
                      className="text-slate-400 hover:text-slate-800"
                    >
                      <ExternalLink size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <DialogFooter>
            <Button data-testid="manage-projects-done-btn" onClick={() => setManaging(null)} style={{ backgroundColor: "var(--brand-primary)" }}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
};

export const Portfolios = () => <Hierarchy kind="portfolio" />;
export const Programmes = () => <Hierarchy kind="programme" />;
